import type * as THREE from 'three'

type ReadbackBuffer = Parameters<THREE.WebGLRenderer['readRenderTargetPixels']>[5]

interface PixelPackContext {
  readonly PIXEL_PACK_BUFFER?: number
  bindBuffer?(target: number, buffer: WebGLBuffer | null): void
}

/**
 * Three r185 的异步 readback 会在等待 fence 期间保留 PIXEL_PACK_BUFFER 绑定。
 * PlayCanvas 会在 readPixels 提交后立即解绑；同步回读前也必须保持同一约束。
 */
export function unbindWebGLPixelPackBuffer(renderer: THREE.WebGLRenderer): void {
  const gl = renderer.getContext() as WebGLRenderingContext & PixelPackContext
  if (typeof gl.PIXEL_PACK_BUFFER !== 'number' || typeof gl.bindBuffer !== 'function') return
  gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
}

/**
 * 对 Three 的 PBO 异步回读补齐 PlayCanvas 的 buffer ownership/state 恢复语义。
 * 解绑不会取消已提交的 readPixels；Three 在 getBufferSubData 前会重新绑定同一 PBO。
 */
export async function readWebGLRenderTargetPixels<T extends ReadbackBuffer>(
  renderer: THREE.WebGLRenderer,
  renderTarget: THREE.WebGLRenderTarget,
  x: number,
  y: number,
  width: number,
  height: number,
  buffer: T,
  activeCubeFaceIndex?: number,
  textureIndex = 0
): Promise<T> {
  const readAsync = renderer.readRenderTargetPixelsAsync?.bind(renderer)
  if (!readAsync) {
    unbindWebGLPixelPackBuffer(renderer)
    renderer.readRenderTargetPixels(
      renderTarget,
      x,
      y,
      width,
      height,
      buffer,
      activeCubeFaceIndex,
      textureIndex
    )
    return buffer
  }

  let pending: Promise<ReadbackBuffer>
  try {
    pending = readAsync(
      renderTarget,
      x,
      y,
      width,
      height,
      buffer,
      activeCubeFaceIndex,
      textureIndex
    )
  } finally {
    // async function 调用会同步执行到首个 await；此时 Three 已提交 readPixels 并绑定 PBO。
    unbindWebGLPixelPackBuffer(renderer)
  }

  try {
    await pending
    return buffer
  } finally {
    // Three r185 在 getBufferSubData 后也没有显式解绑；成功、失败都恢复全局 GL 状态。
    unbindWebGLPixelPackBuffer(renderer)
  }
}
